import type { BlockContextItem, HandlebarsBlockType } from '../../types';

interface BlockMatch {
  type: HandlebarsBlockType;
  variable: string;
  position: number;
  blockId: string;
}

/**
 * Parse block context from plain text by finding unclosed Handlebars blocks.
 * Works for plain text editors (like HtmlSourceEditor) where we can't traverse
 * ProseMirror nodes.
 *
 * @param text - The full text content
 * @param cursorPos - The cursor position in the text
 * @returns Array of enclosing blocks (innermost last)
 */
export function parseBlockContextFromText(
  text: string,
  cursorPos: number
): BlockContextItem[] {
  const textBeforeCursor = text.slice(0, cursorPos);

  // Track all block starts and ends
  const starts: BlockMatch[] = [];

  // Regex to match block starts: {{#blockType variable}} or {{#blockType}}
  // Also handles data attributes format: data-block-type="blockType" data-variable="variable"
  const blockStartRegex = /\{\{#([\w.:]+)(?:\s+([^}]*))?\}\}/g;
  // Also match the span format from editor HTML
  const spanBlockStartRegex = /data-handlebars-block-start[^>]*data-block-type="([\w.:]+)"[^>]*(?:data-variable="([^"]*)")?[^>]*data-block-id="([^"]*)"/g;

  // Regex to match block ends: {{/blockType}}
  const blockEndRegex = /\{\{\/([\w.:]+)\}\}/g;
  // Also match the span format
  const spanBlockEndRegex = /data-handlebars-block-end[^>]*data-block-type="([\w.:]+)"[^>]*data-block-id="([^"]*)"/g;

  let match;
  let blockIdCounter = 0;

  // Find all block starts in plain Handlebars syntax
  while ((match = blockStartRegex.exec(textBeforeCursor)) !== null) {
    starts.push({
      type: match[1] as HandlebarsBlockType,
      variable: match[2]?.trim() || '',
      position: match.index,
      blockId: `text-block-${match.index}-${blockIdCounter++}`,
    });
  }

  // Find all block starts in span format (editor HTML)
  while ((match = spanBlockStartRegex.exec(textBeforeCursor)) !== null) {
    starts.push({
      type: match[1] as HandlebarsBlockType,
      variable: match[2] || '',
      position: match.index,
      blockId: match[3] || `span-block-${match.index}-${blockIdCounter++}`,
    });
  }

  // Sort by position to process in document order
  starts.sort((a, b) => a.position - b.position);

  // Find all block ends and remove matching starts (LIFO order)
  const unmatchedStarts: BlockMatch[] = [];

  // Process starts in order, then remove matches for each end
  for (const start of starts) {
    unmatchedStarts.push(start);
  }

  // Process plain Handlebars block ends
  while ((match = blockEndRegex.exec(textBeforeCursor)) !== null) {
    const endType = match[1];
    // Find last matching start of this type
    for (let i = unmatchedStarts.length - 1; i >= 0; i--) {
      if (unmatchedStarts[i].type === endType) {
        unmatchedStarts.splice(i, 1);
        break;
      }
    }
  }

  // Process span format block ends
  while ((match = spanBlockEndRegex.exec(textBeforeCursor)) !== null) {
    const endBlockId = match[2];
    // Find matching start by blockId
    const matchIndex = unmatchedStarts.findIndex((s) => s.blockId === endBlockId);
    if (matchIndex >= 0) {
      unmatchedStarts.splice(matchIndex, 1);
    }
  }

  // Remaining starts are unclosed blocks (the current context)
  return unmatchedStarts.map((s) => ({
    type: s.type,
    variable: s.variable,
    blockId: s.blockId,
  }));
}

/**
 * Find unclosed block starts that need matching end tags.
 * Useful for suggesting block end completions.
 *
 * @param text - The full text content
 * @param cursorPos - The cursor position in the text
 * @returns Array of block types that are unclosed
 */
export function findUnclosedBlocks(
  text: string,
  cursorPos: number
): Array<{ type: HandlebarsBlockType; variable: string }> {
  const context = parseBlockContextFromText(text, cursorPos);
  return context.map((c) => ({ type: c.type, variable: c.variable }));
}

/**
 * Check if we're inside a specific block type.
 *
 * @param text - The full text content
 * @param cursorPos - The cursor position in the text
 * @param blockType - The block type to check for
 * @returns True if cursor is inside a block of the specified type
 */
export function isInsideBlock(
  text: string,
  cursorPos: number,
  blockType: HandlebarsBlockType
): boolean {
  const context = parseBlockContextFromText(text, cursorPos);
  return context.some((c) => c.type === blockType);
}
